import { useMemo, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import * as api from '../../services/api';
import useAccount from './useAccount';

const useAdmins = () => {
  const { accountId } = useAccount();
  const queryClient = useQueryClient();

  const storeKey = useMemo(() => ['admins', accountId], [accountId]);

  const { isLoading, refetch, data } = useQuery(storeKey, () => api.fetchAdmins({ accountId }));

  const mappedAdmins = useMemo(
    () =>
      (data?.data || []).reduce((all, admin) => {
        all[admin.id] = admin;
        return all;
      }, {}),
    [data?.data]
  );

  const invalidateAdminsAndStats = useCallback(() => {
    queryClient.invalidateQueries(storeKey);
    queryClient.invalidateQueries('employeesStats');
  }, [queryClient, storeKey]);

  const { mutateAsync: createAdmin, isLoading: isCreating } = useMutation(
    (adminData) => api.addAdmin({ accountId, admin: adminData }),
    {
      onSuccess: invalidateAdminsAndStats
    }
  );

  const { mutateAsync: updateAdmin, isLoading: isUpdating } = useMutation(
    ({ adminId, ...adminData }) => api.updateAdmin({ accountId, adminId, admin: adminData }),
    {
      onSuccess: () => queryClient.invalidateQueries(storeKey)
    }
  );

  const { mutateAsync: deleteAdmin, isLoading: isDeleting } = useMutation(
    (adminId) => api.deleteAdmin({ accountId, adminId }),
    {
      onSuccess: invalidateAdminsAndStats
    }
  );

  const { mutateAsync: resendAdminEmail } = useMutation((adminId) => api.resendAdminEmail({ accountId, adminId }));

  return {
    isLoading,
    isCreating,
    isUpdating,
    isDeleting,
    refetch,
    mappedAdmins,
    admins: data?.data || [],
    createAdmin,
    updateAdmin,
    deleteAdmin,
    resendAdminEmail
  };
};

export default useAdmins;
